import { AccountMenu } from '@/shared/components/AppSidebar/AccountMenu';
import { DefaultNavSidebar } from '@/shared/components/AppSidebar/DefaultNavSidebar';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { APP_NAME } from '@/shared/config/app';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import type { BaseDialogProps } from '@/shared/lib/dialogRegistry';
import { useLingui } from '@lingui/react/macro';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useRouterState } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { DialogPortalContainerContext } from '../ui/dialogPortalContext';

/**
 * Full-screen primary navigation on mobile. Registered as the app-wide
 * `mobile-nav` dialog and opened from {@link MobileTopBar} via
 * `openDialog('mobile-nav')`; the dialog registry host injects `open` /
 * `onOpenChange` and owns the mount + two-phase close.
 *
 * Built on the design-system Radix `Dialog` primitive: the portal, body
 * scroll-lock, focus trap, and Escape-to-close come from Radix (per the app
 * convention that the dialog owns Escape) — no `window`/`document` listeners.
 * Selecting a nav item closes it (via `onNavigate`); it also closes on any route
 * change (e.g. sign-out) and when the viewport leaves mobile.
 */
export function MobileNavOverlay({ open, onOpenChange }: BaseDialogProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Pathname at open time — the overlay is mounted by the dialog host on open,
  // so a bare `[pathname]` effect would fire on mount and close it immediately.
  const openedPathname = useRef(pathname);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  // The overlay is mobile-only; close it if the viewport grows while open.
  useEffect(() => {
    if (!isMobile) onOpenChange(false);
  }, [isMobile, onOpenChange]);

  // Close on route change *after* opening (sign-out, or any navigation not routed
  // through a nav item) — but not on the initial mount.
  useEffect(() => {
    if (pathname !== openedPathname.current) onOpenChange(false);
  }, [pathname, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={setPortalContainer}
          data-slot="mobile-nav-overlay"
          className="fixed inset-0 z-50 flex h-svh w-screen flex-col bg-background p-0 text-foreground outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <DialogPortalContainerContext.Provider value={portalContainer}>
            {/* Radix requires a title for the dialog's accessible name; hidden visually. */}
            <DialogTitle className="sr-only">{t`Navigation menu`}</DialogTitle>
            <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border px-4 pt-[env(safe-area-inset-top)]">
              <div className="flex min-w-0 items-center gap-2.5">
                <img src="/assets/Icon.svg" alt="" className="h-7 w-7" />
                <span className="truncate font-semibold text-base tracking-tight">{APP_NAME}</span>
              </div>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="size-10 shrink-0 p-0"
                  aria-label={t`Close menu`}
                >
                  <X size={22} />
                </Button>
              </DialogClose>
            </div>
            <div className="flex min-h-0 flex-1 flex-col pb-[env(safe-area-inset-bottom)]">
              <DefaultNavSidebar mobile onNavigate={() => onOpenChange(false)} />
              <AccountMenu mobile />
            </div>
          </DialogPortalContainerContext.Provider>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
